import React, { useState, useEffect } from "react";
import { Navigate, Link } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import TurnstileWidget from "../common/TurnstileWidget";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import SignupHeader from "../Register/signup_header";
import { userService } from "../../_services";
import Signupleft from "../../images/consumer_login.webp";
import CryptoJS from 'crypto-js';

const formSchema = z.object({
    username: z.string().min(1, { message: "Please enter a valid username" }),
    password: z.string().min(1, { message: "Please enter your password" }),
    remember: z.boolean().optional(),
});

function ConsumerLogIn() {
    const [showPassword, setShowPassword] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileError, setTurnstileError] = useState(false);

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            username: '',
            password: '',
            remember: false,
        },
    });

    useEffect(() => {
        try {
            const cookies = new Cookies();
            const encryptedUsername = cookies.get('_col_uuid');
            const encryptedPassword = cookies.get('__gads');

            if (encryptedUsername && encryptedPassword) {
                const username = CryptoJS.AES.decrypt(encryptedUsername, 'sblw3hn8').toString(CryptoJS.enc.Utf8);
                const password = CryptoJS.AES.decrypt(encryptedPassword, 'sblw3hn8').toString(CryptoJS.enc.Utf8);
                form.setValue('username', username);
                form.setValue('password', password);
                form.setValue('remember', true);
            }
        } catch (error) {
            console.error('Error loading saved credentials:', error);
        }
    }, [form]);

    const saveCredentialsToCookies = (username, password) => {
        try {
            const cookies = new Cookies();
            const encryptedUsername = CryptoJS.AES.encrypt(username, 'sblw3hn8').toString();
            const encryptedPassword = CryptoJS.AES.encrypt(password, 'sblw3hn8').toString();
            cookies.set('_col_uuid', encryptedUsername, { path: '/', maxAge: 2592000 });
            cookies.set('__gads', encryptedPassword, { path: '/', maxAge: 2592000 });
        } catch (error) {
            console.error('Error saving credentials:', error);
        }
    };

    const clearCredentialsCookies = () => {
        try {
            const cookies = new Cookies();
            cookies.remove('_col_uuid', { path: '/' });
            cookies.remove('__gads', { path: '/' });
        } catch (error) {
            console.error('Error clearing credentials:', error);
        }
    };

    const onSubmit = async (values) => {
        if (!turnstileToken) {
            setTurnstileError(true);
            return;
        }
        setTurnstileError(false);

        form.clearErrors('root');

        try {
            // PRESERVE EXACT API CALL - DO NOT CHANGE
            const response = await userService.ConsumerLogin(values.username, values.password);

            if (response && response.length > 0) {
                if (response[0].code === "1") {
                    if (values.remember) {
                        saveCredentialsToCookies(values.username, values.password);
                    } else {
                        clearCredentialsCookies();
                    }

                    localStorage.setItem('ajs_user_id', response[0].userid);
                    localStorage.setItem('_Host_Pro_type', window.btoa('consumer'));
                    localStorage.setItem('hacker_user_detail', response[0].userdetail);

                    window.location.href = '/consumer/summary';
                } else {
                    form.setError('password', { message: response[0].message || 'Invalid username or password' });
                    form.setValue('password', '');
                    setTurnstileToken('');
                }
            } else {
                form.setError('root', { message: 'Login failed. Please try again.' });
            }
        } catch (error) {
            console.error('Login error:', error);
            form.setError('root', { message: 'An error occurred. Please try again.' });
        }
    };

    const signUp = () => {
        const currentUrl = window.location.origin;
        if (currentUrl.toLowerCase().includes("localhost:") ||
            currentUrl.toLowerCase().includes("release-backoffice") ||
            currentUrl.toLowerCase().includes("dev-backoffice") ||
            currentUrl.toLowerCase().includes("shadcn-backoffice")) {
            window.open("https://dev-backoffice.nulifespanrx.com", '_blank');
        } else {
            window.open("https://nulifespanrx.com", '_blank');
        }
    };

    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        const userType = window.atob(localStorage.getItem('_Host_Pro_type'));

        if (userType !== "Admin") {
            if (userType === "consumer") {
                return <Navigate replace to="/consumer/summary" />;
            }
            return <Navigate replace to="/summary" />;
        }
        return <Navigate replace to="/Admin/groups" />;
    }

    const isSubmitting = form.formState.isSubmitting;

    return (
        <div className="min-h-screen bg-secondary">
            <SignupHeader showHowItWorks={false} />

            <div className="w-full mx-auto">
                <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
                    <div className="w-full max-w-[1260px] mx-auto px-4 md:px-8">
                        <div className="flex flex-col md:flex-row items-stretch gap-8 md:gap-16">
                            <div className="flex-1 flex items-center justify-center md:max-w-[689px]">
                                <img
                                    src={Signupleft}
                                    alt="Consumer sign in"
                                    className="w-full h-auto object-contain"
                                />
                            </div>

                            <div className="flex-1 flex items-center justify-center md:max-w-[490px]">
                                <div className="w-full space-y-6">
                                    <div className="space-y-2 text-center md:text-left">
                                        <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                                            Welcome back
                                        </h1>
                                        <p className="text-md text-foreground opacity-70">
                                            Please sign in to continue
                                        </p>
                                    </div>

                                    <Form {...form}>
                                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                            {form.formState.errors.root && (
                                                <p className="text-sm font-medium text-destructive">
                                                    {form.formState.errors.root.message}
                                                </p>
                                            )}

                                            <FormField
                                                control={form.control}
                                                name="username"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <div className="relative">
                                                                <Input
                                                                    {...field}
                                                                    disabled={isSubmitting}
                                                                    placeholder=" "
                                                                    className="peer h-12"
                                                                />
                                                                <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                                                    Username
                                                                </FormLabel>
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="password"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <div className="relative">
                                                                <Input
                                                                    {...field}
                                                                    type={showPassword ? "text" : "password"}
                                                                    disabled={isSubmitting}
                                                                    placeholder=" "
                                                                    className="peer pr-10 h-12"
                                                                />
                                                                <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                                                    Password
                                                                </FormLabel>
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                                                >
                                                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                                </Button>
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <div className="flex items-center justify-between py-2">
                                                <FormField
                                                    control={form.control}
                                                    name="remember"
                                                    render={({ field }) => (
                                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value}
                                                                    onCheckedChange={field.onChange}
                                                                    disabled={isSubmitting}
                                                                />
                                                            </FormControl>
                                                            <div className="space-y-1 leading-none">
                                                                <FormLabel className="text-sm font-normal text-foreground">
                                                                    Remember me
                                                                </FormLabel>
                                                            </div>
                                                        </FormItem>
                                                    )}
                                                />
                                                <Link to="/forgotpassword" className="text-sm text-accent hover:text-accent/90">
                                                    Forgot Password?
                                                </Link>
                                            </div>

                                            <div className="w-full">
                                                <TurnstileWidget
                                                    onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(false); }}
                                                    onError={() => { setTurnstileToken(''); setTurnstileError(true); }}
                                                    onExpire={() => { setTurnstileToken(''); setTurnstileError(true); }}
                                                />
                                                {turnstileError && (
                                                    <p className="text-sm font-medium text-destructive mt-1">
                                                        Please complete the verification above
                                                    </p>
                                                )}
                                            </div>

                                            <Button type="submit" variant="secondary" size="lg" className="w-auto" disabled={isSubmitting}>
                                                {isSubmitting ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Signing in...
                                                    </>
                                                ) : (
                                                    'Sign In'
                                                )}
                                            </Button>
                                        </form>
                                    </Form>

                                    <div className="mt-6 text-center md:text-left space-y-2">
                                        <p className="text-md text-foreground opacity-70">
                                            Don&apos;t have an account?{' '}
                                            <button
                                                type="button"
                                                onClick={signUp}
                                                className="text-accent hover:no-underline hover:text-foreground font-medium bg-transparent border-0 p-0 cursor-pointer"
                                            >
                                                Sign Up here
                                            </button>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConsumerLogIn;
